import { authFetch } from './auth-fetch';
import type { CurrentUser } from '../stores/auth';

/**
 * The API as this frontend uses it, in one place.
 *
 * Deliberately thin: no client-side modelling, no renaming of fields. What comes
 * back is what `12` describes, so a screen reading `company.slug` is reading the
 * documented contract rather than something this layer invented — and the
 * generated `schema.d.ts` stays the reference for the shapes.
 */

export interface Company {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  industry: string | null;
  mission: string | null;
  logo_url: string | null;
  status: 'active' | 'paused' | 'archived';
  settings: Record<string, unknown>;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  counters?: Record<string, number>;
}

export interface Credential {
  id: string;
  type: 'anthropic_api_key' | 'claude_subscription_oauth' | 'openai_api_key' | 'openai_subscription_oauth' | 'github_pat' | 'github_app_installation';
  label: string;
  hint: string;
  status: 'active' | 'invalid' | 'expired' | 'revoked';
  is_default: boolean;
  metadata: Record<string, unknown>;
  last_verified_at: string | null;
}

export interface AuditEntry {
  id: string;
  actor_type: string;
  actor_id: string | null;
  action: string;
  subject_type: string;
  subject_id: string | null;
  created_at: string;
}

export interface HealthCheck {
  key: string;
  status: 'ok' | 'warning' | 'error' | 'not_applicable';
  detail: string;
}

export interface Page<T> {
  data: T[];
  meta: { limit: number; has_more: boolean; next_cursor: string | null; total_estimate: number };
}

export const auth = {
  register: (body: { email: string; password: string; name: string }) =>
    authFetch<void>('/auth/register', { method: 'POST', body }),
  verifyEmail: (token: string) => authFetch<void>('/auth/verify-email', { method: 'POST', body: { token } }),
  forgotPassword: (email: string) => authFetch<void>('/auth/forgot-password', { method: 'POST', body: { email } }),
  resetPassword: (body: { token: string; password: string }) =>
    authFetch<void>('/auth/reset-password', { method: 'POST', body }),
  login: (body: { email: string; password: string; mfa_code?: string; captcha?: string }) =>
    authFetch<{ access_token: string; user: CurrentUser }>('/auth/login', { method: 'POST', body }),
  me: () => authFetch<CurrentUser>('/auth/me'),
  updateProfile: (body: Record<string, unknown>) => authFetch<CurrentUser>('/auth/me', { method: 'PATCH', body }),
  logout: () => authFetch<void>('/auth/logout', { method: 'POST', body: {} }),
};

export const companies = {
  list: () => authFetch<Page<Company>>('/companies'),
  create: (body: { name: string; slug: string; description?: string; industry?: string; mission?: string }) =>
    authFetch<Company>('/companies', { method: 'POST', body }),
  get: (id: string) => authFetch<Company>(`/companies/${id}`),
  update: (id: string, body: Record<string, unknown>) =>
    authFetch<Company>(`/companies/${id}`, { method: 'PATCH', body }),
  updateSettings: (id: string, body: Record<string, unknown>) =>
    authFetch<Company>(`/companies/${id}/settings`, { method: 'PATCH', body }),
  archive: (id: string) => authFetch<Company>(`/companies/${id}/archive`, { method: 'POST', body: {} }),
  restore: (id: string) => authFetch<Company>(`/companies/${id}/restore`, { method: 'POST', body: {} }),
  /**
   * `14 §9.2`: cancela todo lo que está en vuelo y pausa la empresa. Responde
   * 202 porque las ejecuciones reclamadas se *señalan* — el runtime las tira
   * en segundos, pero siguen vivas cuando se escribe la respuesta.
   */
  emergencyStop: (id: string) =>
    authFetch<{ cancelled_runs: number }>(`/companies/${id}/emergency-stop`, { method: 'POST', body: {} }),
  resume: (id: string) =>
    authFetch<{ company: Company; tasks_rescheduled: number }>(`/companies/${id}/resume`, {
      method: 'POST',
      body: {},
    }),
  dashboard: (id: string) => authFetch<Record<string, unknown>>(`/companies/${id}/dashboard`),
  health: (id: string) => authFetch<{ status: string; checks: HealthCheck[] }>(`/companies/${id}/health`),
};

export const credentials = {
  list: (companyId: string) => authFetch<{ data: Credential[] }>(`/companies/${companyId}/credentials`),
  create: (companyId: string, body: { type: string; label: string; secret: string; is_default?: boolean }) =>
    authFetch<Credential>(`/companies/${companyId}/credentials`, { method: 'POST', body }),
  /**
   * `12 §3`: label and `is_default`, the two things about a stored credential
   * that can change without the secret changing. Promotion is a transaction
   * server-side — one default per type — and demotion is not its mirror image:
   * leaving a type with no default is legal, and then a run falls back to the
   * other type.
   */
  update: (companyId: string, id: string, body: { label?: string; is_default?: boolean }) =>
    authFetch<Credential>(`/companies/${companyId}/credentials/${id}`, { method: 'PATCH', body }),
  rotate: (companyId: string, id: string, secret: string) =>
    authFetch<Credential>(`/companies/${companyId}/credentials/${id}/rotate`, { method: 'POST', body: { secret } }),
  verify: (companyId: string, id: string) =>
    authFetch<{ credential: Credential; check: { valid: boolean; provider_answered: boolean; error: string | null } }>(
      `/companies/${companyId}/credentials/${id}/verify`,
      { method: 'POST', body: {} },
    ),
  revoke: (companyId: string, id: string) =>
    authFetch<Credential>(`/companies/${companyId}/credentials/${id}`, { method: 'DELETE' }),
};

export const audit = {
  list: (companyId: string, params: URLSearchParams = new URLSearchParams()) =>
    authFetch<Page<AuditEntry>>(`/companies/${companyId}/audit-logs?${params.toString()}`),
};

export interface OpenaiLoginAttempt {
  id: string; status: 'pending' | 'connected' | 'cancelled' | 'expired' | 'rejected' | 'temporary_error';
  expires_at: number; verification_url?: string; user_code?: string; credential_id?: string | null; error?: string;
}
export const openaiLogin = {
  start: (companyId: string, body: { label: string; credential_id?: string }) => authFetch<OpenaiLoginAttempt>(`/companies/${companyId}/credentials/openai/login`, { method: 'POST', body }),
  status: (companyId: string, id: string) => authFetch<OpenaiLoginAttempt>(`/companies/${companyId}/credentials/openai/login/${id}`),
  cancel: (companyId: string, id: string) => authFetch<OpenaiLoginAttempt>(`/companies/${companyId}/credentials/openai/login/${id}`, { method: 'DELETE' }),
};
